import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Typography, Box, Paper, CircularProgress, Chip,
    Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { useSmartAlertRunDetail } from '../hooks/useSmartAlerts';

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('hr-HR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
};

const sentimentColors = {
    positive: { bg: '#e8f5e9', color: '#2e7d32' },
    neutral: { bg: '#f5f5f5', color: '#666' },
    negative: { bg: '#ffebee', color: '#c62828' },
};

const relevanceDots = (score) => {
    if (score == null) return '—';
    const filled = Math.round(score / 20);
    return '●'.repeat(filled) + '○'.repeat(5 - filled);
};

export default function SmartAlertRunDetail() {
    const { runId } = useParams();
    const navigate = useNavigate();
    const { data, isLoading } = useSmartAlertRunDetail(runId);

    useEffect(() => { document.title = 'Detalji izvršavanja | Media Scanner'; }, []);

    if (isLoading || !data) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    const { run, articles } = data;
    const sentArticles = articles.filter(a => a.was_sent);
    const filteredArticles = articles.filter(a => !a.was_sent);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
            <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
                    Detalji izvršavanja
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    <Chip label={`Status: ${run.status}`} size="small"
                        sx={{ fontWeight: 600, backgroundColor: run.status === 'completed' ? '#e8f5e9' : '#ffebee', color: run.status === 'completed' ? '#2e7d32' : '#c62828' }}
                    />
                    <Chip label={`Pronađeno: ${run.articles_matched ?? 0}`} size="small" variant="outlined" />
                    <Chip label={`Klastera: ${run.clusters_formed ?? 0}`} size="small" variant="outlined" />
                    <Chip label={`Poslano: ${run.articles_sent ?? 0}`} size="small" variant="outlined" />
                    <Chip label={`Filtrirano: ${run.articles_filtered ?? 0}`} size="small" variant="outlined" />
                    <Typography variant="body2" sx={{ color: '#999', alignSelf: 'center' }}>
                        {formatDate(run.timestamp_started)}
                    </Typography>
                </Box>

                {sentArticles.length > 0 && (
                    <Paper sx={{ p: 2, borderRadius: 2, mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1, fontSize: '1rem' }}>
                            Poslani članci ({sentArticles.length})
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Naslov</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Portal</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Relevantnost</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Sentiment</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Pojmovi</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sentArticles.map((a) => {
                                    const sc = sentimentColors[a.sentiment_term] || sentimentColors.neutral;
                                    return (
                                        <TableRow key={a.id}>
                                            <TableCell sx={{ maxWidth: 350 }}>
                                                <Typography
                                                    variant="body2"
                                                    component="a"
                                                    href={a.article_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ color: '#0079bf', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, fontWeight: a.is_representative ? 600 : 400 }}
                                                >
                                                    {a.article_title || '—'}
                                                </Typography>
                                                {a.is_continuation && (
                                                    <Chip label="nastavak" size="small" sx={{ ml: 1, fontSize: 10, height: 18 }} />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{a.source_name || '—'}</TableCell>
                                            <TableCell align="center" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                                                {relevanceDots(a.relevance_score)}
                                            </TableCell>
                                            <TableCell>
                                                {a.sentiment_term && (
                                                    <Typography variant="caption" sx={{ px: 1, py: 0.25, borderRadius: 1, fontWeight: 600, backgroundColor: sc.bg, color: sc.color }}>
                                                        {a.sentiment_term}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 12, color: '#666' }}>{a.matched_terms || '—'}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Paper>
                )}

                {filteredArticles.length > 0 && (
                    <Paper sx={{ p: 2, borderRadius: 2, mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#999', mb: 1, fontSize: '1rem' }}>
                            Filtrirani članci ({filteredArticles.length})
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Naslov</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Portal</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Razlog filtriranja</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredArticles.map((a) => (
                                    <TableRow key={a.id} sx={{ opacity: 0.6 }}>
                                        <TableCell sx={{ maxWidth: 350 }}>
                                            <Typography
                                                variant="body2"
                                                component="a"
                                                href={a.article_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ color: '#666', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                            >
                                                {a.article_title || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{a.source_name || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: 12, color: '#999' }}>
                                            {a.filter_reason === 'heuristic_discard' ? 'Heuristika' :
                                             a.filter_reason === 'llm_not_relevant' ? 'AI: nije relevantno' :
                                             a.filter_reason || '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Typography
                        variant="body2"
                        sx={{ color: '#0079bf', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => navigate(-1)}
                    >
                        Natrag
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
